import { Suggestion } from "@b2bportal/lodging-api";
import { trackEvent } from "@hopper-b2b/api";
import {
  PATH_HOTELS_BOOK_AVAILABILITY,
  PATH_STAYS_AVAILABILITY,
} from "@hopper-b2b/hopper-utils";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  buildSearchParams,
  placeLabelAndSublabelToUrl,
} from "@hopper-b2b/lodging-utils";
import { GuestsSelection, TripCategory } from "@hopper-b2b/types";
import { Grid } from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIsRoomsEnabled } from "../../context";
import { CalenderPickerButton } from "../common/CalendarPickerButton";
import { GuestPicker } from "./GuestPicker";
import { HotelLocationAutoComplete } from "./HotelLocationAutocomplete";
import { ActionButton } from "@hopper-b2b/ui";

export type HotelSearchFormHandlers = {
  onDestinationChange?: (destination: Suggestion) => void;
  onCheckingDateChange?: (date: Date) => void;
  onCheckoutDateChange?: (date: Date | undefined) => void;
  onGuestCountChange?: (guestCount: GuestsSelection) => void;
};
export type HotelSearchProps = {
  changeHandlers?: HotelSearchFormHandlers;
  enableRooms?: boolean;
  initialAdultGuests?: number;
  initialChildGuests?: number[];
  initialRooms?: number;
  initialLocation?: Suggestion;
  initialCheckinDate?: string;
  initialCheckoutDate?: string;
  initialPets?: number;
  dateFormat?: string;
  onSearch?: (path: string) => void;
  onOpen?: () => void;
  skipHotelTappedEntry?: boolean;
  staysEnabled?: boolean;
};

export const HotelSearch = ({
  changeHandlers,
  enableRooms: enableRoomsProps = true,
  initialAdultGuests = 1,
  initialChildGuests = [],
  initialRooms = 1,
  initialPets = 0,
  initialLocation,
  onSearch,
  onOpen,
  initialCheckinDate,
  initialCheckoutDate,
  dateFormat,
  skipHotelTappedEntry,
  staysEnabled,
}: HotelSearchProps) => {
  const { t } = useI18nContext();

  const enableRoomsFlag = useIsRoomsEnabled();
  const enableRooms = enableRoomsProps ?? enableRoomsFlag;

  const [destination, setDestination] = useState<Suggestion | undefined>(
    initialLocation
  );
  const [checkinDate, setCheckinDate] = useState<string | undefined>(
    initialCheckinDate
  );
  const [checkoutDate, setCheckoutDate] = useState<string | undefined>(
    initialCheckoutDate
  );
  const [guestCount, setGuestCount] = useState<GuestsSelection>({
    adults: initialAdultGuests,
    children: initialChildGuests,
    rooms: initialRooms,
    pets: initialPets,
  });

  const [hotelTappedEntry, setHotelTappedEntry] = useState<boolean | null>(
    true
  );

  useEffect(() => {
    setDestination(initialLocation);
  }, [initialLocation]);

  const destinationCode = useMemo(() => {
    if (destination) {
      const { label, subLabel } = destination;
      return placeLabelAndSublabelToUrl(label, subLabel);
    }
    return undefined;
  }, [destination]);

  const isSearchReady = useMemo(
    () => destination && checkinDate && checkoutDate,
    [checkinDate, checkoutDate, destination]
  );

  const searchUrl = useMemo(() => {
    const searchParams = buildSearchParams({
      fromDate: checkinDate,
      untilDate: checkoutDate,
      adultsCount: guestCount.adults,
      childrenCount: guestCount.children,
      roomsCount: guestCount.rooms,
      pets: guestCount.pets,
      enableRooms,
    });

    const baseAvailabilityPath = staysEnabled
      ? PATH_STAYS_AVAILABILITY
      : PATH_HOTELS_BOOK_AVAILABILITY;
    return `${baseAvailabilityPath}${destinationCode}?${searchParams.toString()}`;
  }, [
    guestCount.adults,
    guestCount.children,
    guestCount.rooms,
    guestCount.pets,
    checkinDate,
    checkoutDate,
    destinationCode,
    enableRooms,
    staysEnabled,
  ]);

  const getTrackingProperties = useCallback(
    ({
      checkin = checkinDate,
      checkout = checkoutDate,
      guest = guestCount,
      dest = destination,
    }) => ({
      ...{
        properties: {
          adults_count: guest.adults,
          children_count: guest.children.length,
          check_in_date: dayjs(checkin).format("YYYY-MM-DD"),
          check_out_date: dayjs(checkout).format("YYYY-MM-DD"),
          advance: dayjs(checkin).diff(dayjs(), "day") || null,
          los: dayjs(checkout).diff(dayjs(checkin), "day") || null,
        },
      },
      ...{
        properties: dest?.trackingPropertiesV2.properties,
        encryptedProperties: dest?.trackingPropertiesV2.encryptedProperties
          ? [dest.trackingPropertiesV2.encryptedProperties]
          : [],
      },
    }),
    [checkinDate, checkoutDate, guestCount, destination]
  );

  const handleSearch = useCallback(() => {
    onSearch ? onSearch?.(searchUrl) : (window.location.href = searchUrl);
  }, [onSearch, searchUrl]);

  const handleDestinationChange = useCallback(
    (dest: Suggestion) => {
      setDestination(dest);

      changeHandlers?.onDestinationChange?.(dest);
    },
    [changeHandlers]
  );

  const handleCheckinDateChange = useCallback(
    (date: Date) => {
      const trackingProperties = {};
      if (date) {
        setCheckinDate(date.toString());
        trackingProperties["checkin"] = date.toString();
        changeHandlers?.onCheckingDateChange?.(date);
        if (checkoutDate) {
          setCheckoutDate(undefined);
          trackingProperties["checkout"] = undefined;
          changeHandlers?.onCheckoutDateChange?.(undefined);
        }
        trackEvent({
          eventName: "hotel_selected_calendar_date",
          ...getTrackingProperties(trackingProperties),
        });
      }
    },
    [changeHandlers, checkoutDate, getTrackingProperties]
  );

  const handleCheckoutDateChange = useCallback(
    (date: Date) => {
      if (date) {
        setCheckoutDate(date.toString());
        changeHandlers?.onCheckoutDateChange?.(date);
        trackEvent({
          eventName: "hotel_selected_calendar_date",
          ...getTrackingProperties({ checkout: date.toString() }),
        });
      }
    },
    [changeHandlers, getTrackingProperties]
  );

  const updateGuestCount = useCallback(
    (guestCount: GuestsSelection) => {
      setGuestCount(guestCount);

      changeHandlers?.onGuestCountChange?.(guestCount);
    },
    [changeHandlers]
  );

  const handleHotelTappedEntry = useCallback(() => {
    if (hotelTappedEntry && !skipHotelTappedEntry) {
      trackEvent({
        eventName: "hotel_tapped_entry",
        properties: undefined,
      });
      setHotelTappedEntry(false);
    }
  }, [hotelTappedEntry, setHotelTappedEntry, skipHotelTappedEntry]);

  const onGuestPickerClick = useCallback(() => {
    handleHotelTappedEntry();
  }, [handleHotelTappedEntry]);

  const onCalendarClick = useCallback(() => {
    trackEvent({
      eventName: "hotel_viewed_calendar",
      ...getTrackingProperties({}),
    });

    handleHotelTappedEntry();
  }, [getTrackingProperties, handleHotelTappedEntry]);

  const onComplete = useCallback(() => {
    trackEvent({
      eventName: "hotel_tapped_select_dates",
      ...getTrackingProperties({}),
    });
  }, [getTrackingProperties]);

  const onAutocompleteOpen = useCallback(() => {
    onOpen?.();
    handleHotelTappedEntry();
  }, [onOpen, handleHotelTappedEntry]);

  return (
    <Grid container spacing={2} direction="row" alignItems="stretch">
      <Grid item xs={12} lg={3}>
        <HotelLocationAutoComplete
          id="destination"
          label={t("whereAreYouStaying")}
          onChange={handleDestinationChange}
          onOpen={onAutocompleteOpen}
          defaultValue={initialLocation}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={4} className="hotel-search-container">
        <CalenderPickerButton
          departureDate={checkinDate ? dayjs(checkinDate).toDate() : null}
          returnDate={checkoutDate ? dayjs(checkoutDate).toDate() : null}
          tripCategory={TripCategory.ROUND_TRIP}
          setDepartureDate={handleCheckinDateChange}
          setReturnDate={handleCheckoutDateChange}
          startLabel={t("checkin") ?? undefined}
          endLabel={t("checkout") ?? undefined}
          headerTitle={t("hotelCalendarHeaderTitle")}
          onCalendarClick={onCalendarClick}
          onComplete={onComplete}
          dateFormat={dateFormat}
          preventSameDateSelection
        />
      </Grid>
      <Grid item xs={12} sm={3} lg={3}>
        <GuestPicker
          initialGuestCount={guestCount}
          updateGuestCount={updateGuestCount}
          enableRooms={enableRooms}
          handleClick={onGuestPickerClick}
          enableStays={staysEnabled}
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={3}
        lg={2}
        className="hotel-search-button-container"
      >
        <ActionButton
          onClick={handleSearch}
          fullWidth
          disabled={!isSearchReady}
          size="large"
          message={t("searchButton")}
        />
      </Grid>
    </Grid>
  );
};
